import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import styles from "./passcode-otp-input.module.scss";
import { OTPInput } from "../../../components";
import { maskEmail, maskPhoneNumber } from "../../forgot-password/util";

interface PasscodeOTPInputProps {
	value: string;
	onChange: (value: string) => void;
	onResend: () => void;
	otpLength?: number;
	resendTimerSeconds?: number;
	disabled?: boolean;
	error?: boolean;
	sendToEmail: boolean;
	contact: string;
	dialCode: string;
	renderSeparator: boolean;
	className?: string;
}

const PasscodeOTPInput: React.FC<PasscodeOTPInputProps> = ({
	value,
	onChange,
	onResend,
	otpLength = 6,
	resendTimerSeconds = 180,
	disabled = false,
	error = false,
	sendToEmail,
	contact,
	dialCode = "",
	renderSeparator = true,
	className
}) => {
	const { t } = useTranslation();
	const [resendTimer, setResendTimer] = useState(resendTimerSeconds);

	useEffect(() => {
		if (resendTimer > 0) {
			const timer = setTimeout(() => setResendTimer(prev => prev - 1), 1000);
			return () => clearTimeout(timer);
		}
	}, [resendTimer]);

	const handleResend = () => {
		setResendTimer(resendTimerSeconds);
		onResend();
	};

	return (
		<div className={`${styles.container}${className ? ` ${className}` : ""}`}>
			<div className={styles.header}>
				<span className={styles.title}> {t("auth.otpVerification")}</span>
				<span className={styles.sendMessage}>
					{sendToEmail ? t("auth.enterOtpSentToEmail") : t("auth.enterOtpSentToPhone")}{" "}
					<span className={styles.contact}>
						{sendToEmail ? maskEmail(contact) : maskPhoneNumber(contact, dialCode)}
					</span>
				</span>
			</div>
			<div className={styles.otpSection}>
				<OTPInput
					value={value}
					onChange={onChange}
					disabled={disabled}
					error={error}
					length={otpLength}
					renderSeparator={(index) => index === 2 && renderSeparator ? <span className={styles.separator}>—</span> : null}
				/>
			</div>
			<div className={styles.resendSection}>
				<p className={styles.resendText}>
					{t("common.labels.didntReceiveCode")}{" "}
					{resendTimer > 0 ? (
						<span> {t("auth.web.resendCodeIn")} <span className={styles.timer}>{resendTimer}s</span> </span>
					) : (
						<button type="button" className={styles.resendButton} onClick={handleResend}>
							{t("auth.web.resend")}
						</button>
					)}
				</p>
			</div>
		</div>
	);
};

export default PasscodeOTPInput;
